import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { Menu, Role, User } from '../../models/api-response.model';
import { MenuService } from '../../services/menu_service';
import { RoleManagementService } from '../../services/role_management_service';
import { UserManagementService, UserMenuPrivilegePayload } from '../../services/user_management_service';

interface PrivilegeState {
  can_read: boolean;
  can_write: boolean;
}

interface MenuPrivilegeRow extends Menu, PrivilegeState {
  level: 'Parent' | 'Child';
  hasChildren: boolean;
}

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(UserManagementService);
  private readonly roleService = inject(RoleManagementService);
  private readonly menuService = inject(MenuService);

  readonly users = signal<User[]>([]);
  readonly roles = signal<Role[]>([]);
  readonly menus = signal<Menu[]>([]);
  readonly searchTerm = signal('');
  readonly isFormOpen = signal(false);
  readonly isPrivilegeModalOpen = signal(false);
  readonly privilegeUser = signal<User | null>(null);
  readonly privilegeState = signal<Record<number, PrivilegeState>>({});
  readonly openActionUserId = signal<number | null>(null);
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);
  readonly actionMenuPosition = signal({ top: 0, left: 0 });
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);
  editingUserId: number | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  readonly filteredUsers = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.users();
    }
    return this.users().filter((user) => {
      const values = [user.name, user.lastname, user.full_name, user.email, user.mobile || ''];
      return values.some((value) => value.toLowerCase().includes(term));
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredUsers().length / this.pageSize())));
  readonly paginatedUsers = computed(() => {
    const currentPage = Math.min(this.currentPage(), this.totalPages());
    const start = (currentPage - 1) * this.pageSize();
    return this.filteredUsers().slice(start, start + this.pageSize());
  });

  readonly privilegeRows = computed<MenuPrivilegeRow[]>(() => {
    const state = this.privilegeState();
    const sortMenus = (items: Menu[]) => [...items].sort((first, second) => first.order_no - second.order_no || first.name.localeCompare(second.name));
    const parents = sortMenus(this.menus().filter((menu) => menu.parent_menu_id === null));
    const children = sortMenus(this.menus().filter((menu) => menu.parent_menu_id !== null));
    const childrenByParent = new Map<number, Menu[]>();
    children.forEach((menu) => {
      const parentId = menu.parent_menu_id || 0;
      childrenByParent.set(parentId, [...(childrenByParent.get(parentId) || []), menu]);
    });
    const parentIdsWithChildren = new Set(children.map((menu) => menu.parent_menu_id).filter((parentId): parentId is number => parentId !== null));
    const rows: MenuPrivilegeRow[] = [];
    const appendRow = (menu: Menu, level: 'Parent' | 'Child') => {
      rows.push({
        ...menu,
        level,
        hasChildren: parentIdsWithChildren.has(menu.id),
        can_read: state[menu.id]?.can_read || false,
        can_write: state[menu.id]?.can_write || false
      });
    };
    parents.forEach((parent) => {
      appendRow(parent, 'Parent');
      (childrenByParent.get(parent.id) || []).forEach((child) => appendRow(child, 'Child'));
    });
    children.filter((menu) => !parents.some((parent) => parent.id === menu.parent_menu_id)).forEach((menu) => appendRow(menu, 'Child'));
    return rows;
  });

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    lastname: ['', [Validators.required, Validators.minLength(1)]],
    user_category: ['PORTAL', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    mobile: [''],
    profile_image: [''],
    role_id: [0, [Validators.required, Validators.min(1)]],
    account_active: [true]
  });

  ngOnInit(): void {
    this.load();
  }

  openCreateForm(): void {
    this.editingUserId = null;
    this.openActionUserId.set(null);
    this.form.reset({
      name: '',
      lastname: '',
      user_category: 'PORTAL',
      email: '',
      mobile: '',
      profile_image: '',
      role_id: 0,
      account_active: true
    });
    this.isFormOpen.set(true);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showToast('Please fill all required fields correctly.', 'error');
      return;
    }

    const rawValue = this.form.getRawValue();
    const payload = {
      name: rawValue.name,
      lastname: rawValue.lastname,
      user_category: rawValue.user_category,
      email: rawValue.email,
      mobile: rawValue.mobile || null,
      profile_image: rawValue.profile_image || null,
      role_id: rawValue.role_id,
      account_active: rawValue.account_active
    };

    const request = this.editingUserId
      ? this.userService.update(this.editingUserId, payload)
      : this.userService.create(payload);

    request.subscribe({
      next: (response) => {
        this.showToast(response.message, 'success');
        this.closeForm();
        this.loadUsers();
      },
      error: (error: HttpErrorResponse) => this.showToast(this.getApiMessage(error), 'error')
    });
  }

  edit(user: User): void {
    this.editingUserId = user.id;
    this.openActionUserId.set(null);
    this.form.reset({
      name: user.name,
      lastname: user.lastname,
      user_category: user.user_category,
      email: user.email,
      mobile: user.mobile || '',
      profile_image: user.profile_image || '',
      role_id: user.role_id,
      account_active: user.account_active
    });
    this.isFormOpen.set(true);
  }

  closeForm(): void {
    this.editingUserId = null;
    this.isFormOpen.set(false);
    this.form.reset({
      name: '',
      lastname: '',
      user_category: 'PORTAL',
      email: '',
      mobile: '',
      profile_image: '',
      role_id: 0,
      account_active: true
    });
  }

  toggleActions(userId: number, event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.actionMenuPosition.set({
      top: rect.bottom + 6,
      left: Math.max(12, rect.right - 216)
    });
    this.openActionUserId.set(this.openActionUserId() === userId ? null : userId);
  }

  toggleAccount(user: User): void {
    const request = user.account_active ? this.userService.deactivate(user.id) : this.userService.activate(user.id);
    request.subscribe({
      next: (response) => {
        this.showToast(response.message, 'success');
        this.loadUsers();
      },
      error: (error: HttpErrorResponse) => this.showToast(this.getApiMessage(error), 'error')
    });
  }

  regeneratePassword(user: User): void {
    this.userService.resetPassword(user.id).subscribe({
      next: (response) => {
        this.openActionUserId.set(null);
        this.showToast(response.message, 'success');
        this.loadUsers();
      },
      error: (error: HttpErrorResponse) => this.showToast(this.getApiMessage(error), 'error')
    });
  }

  openPrivilegeModal(user: User): void {
    this.openActionUserId.set(null);
    this.privilegeUser.set(user);
    this.isPrivilegeModalOpen.set(true);
    this.loadMenus();
    this.userService.getMenuPrivileges(user.id).subscribe((response) => {
      const privileges = response.value === '' ? [] : response.value;
      const nextState: Record<number, PrivilegeState> = {};
      privileges.forEach((privilege) => {
        nextState[privilege.menu_id] = {
          can_read: privilege.can_read || privilege.can_write,
          can_write: privilege.can_write
        };
      });
      this.privilegeState.set(nextState);
    });
  }

  closePrivilegeModal(): void {
    this.isPrivilegeModalOpen.set(false);
    this.privilegeUser.set(null);
    this.privilegeState.set({});
  }

  togglePrivilege(menuId: number, permission: 'read' | 'write', checked: boolean): void {
    const current = this.privilegeState()[menuId] || { can_read: false, can_write: false };
    const nextState = { ...this.privilegeState() };
    if (permission === 'write') {
      nextState[menuId] = {
        can_read: checked ? true : current.can_read,
        can_write: checked
      };
    } else {
      nextState[menuId] = {
        can_read: checked,
        can_write: checked ? current.can_write : false
      };
    }
    this.privilegeState.set(nextState);
  }

  savePrivileges(): void {
    const user = this.privilegeUser();
    if (!user) {
      return;
    }
    const assignableMenuIds = new Set(this.privilegeRows().filter((menu) => !menu.hasChildren).map((menu) => menu.id));
    const privileges: UserMenuPrivilegePayload[] = Object.entries(this.privilegeState())
      .map(([menuId, privilege]) => ({
        menu_id: Number(menuId),
        can_read: privilege.can_read || privilege.can_write,
        can_write: privilege.can_write
      }))
      .filter((privilege) => assignableMenuIds.has(privilege.menu_id))
      .filter((privilege) => privilege.can_read || privilege.can_write);
    this.userService.saveMenuPrivileges(user.id, privileges).subscribe({
      next: (response) => {
        this.showToast(response.message, 'success');
        this.closePrivilegeModal();
      },
      error: (error: HttpErrorResponse) => this.showToast(this.getApiMessage(error), 'error')
    });
  }

  deleteUser(user: User): void {
    this.openActionUserId.set(null);
    this.userService.delete(user.id).subscribe({
      next: (response) => {
        this.showToast(response.message, 'success');
        this.loadUsers();
      },
      error: (error: HttpErrorResponse) => this.showToast(this.getApiMessage(error), 'error')
    });
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  formatDateTime(value: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const pad = (part: number) => part.toString().padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
    this.openActionUserId.set(null);
  }

  private load(): void {
    this.loadUsers();
    this.roleService.list().subscribe((response) => {
      this.roles.set(response.value === '' ? [] : response.value);
    });
  }

  private loadUsers(): void {
    this.userService.list().subscribe((response) => {
      this.users.set(response.value === '' ? [] : response.value);
    });
  }

  private loadMenus(): void {
    if (this.menus().length > 0) {
      return;
    }
    this.menuService.list().subscribe((response) => {
      this.menus.set(this.getMenuItems(response.value));
    });
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toast.set({ message, type });
    this.toastTimer = setTimeout(() => this.toast.set(null), 3500);
  }

  private getApiMessage(error: HttpErrorResponse): string {
    const body = error.error as { message?: string } | null;
    return body?.message || 'Request failed. Please try again.';
  }

  private getMenuItems(value: unknown): Menu[] {
    if (!value || value === '') {
      return [];
    }

    if (Array.isArray(value)) {
      return value as Menu[];
    }

    if (typeof value === 'object' && value !== null && 'items' in value) {
      return (value as { items?: Menu[] }).items || [];
    }

    return [];
  }
}
